interface DateParts {
  readonly year: string;
  readonly month: string;
  readonly day: string;
  readonly hour: string;
  readonly minute: string;
  readonly second: string;
}
export function resolveTimeZone(candidate: unknown): string {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const timeZone = typeof candidate === 'string' && candidate.trim()
    ? candidate.trim()
    : browserTimeZone;

  try {
    new Intl.DateTimeFormat('en', { timeZone }).format(0);
    return timeZone;
  } catch {
    return 'UTC';
  }
}

export function resolveDateLocale(candidate: unknown): string {
  const locale = typeof candidate === 'string' && candidate.trim() ? candidate.trim() : 'it';
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([locale]).length ? locale : 'it';
  } catch {
    return 'it';
  }
}

export function toValidDate(value: unknown): Date | null {
  const date = value instanceof Date
    ? new Date(value.getTime())
    : new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateInTimeZone(
  value: unknown,
  format = 'L',
  timeZoneCandidate?: unknown,
  localeCandidate?: unknown,
): string {
  const date = toValidDate(value);
  if (!date) {
    return 'Invalid date';
  }

  const timeZone = resolveTimeZone(timeZoneCandidate);
  const locale = resolveDateLocale(localeCandidate);
  const parts = numericParts(date, timeZone);

  switch (format) {
    case 'YYYY-MM-DD HH:mm:ss':
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
    case 'YYYY-MM-DD HH:mm':
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
    case 'YYYY-MM-DD':
      return `${parts.year}-${parts.month}-${parts.day}`;
    case 'DD/MM/YYYY':
      return `${parts.day}/${parts.month}/${parts.year}`;
    case 'HH:mm':
    case 'LT':
      return `${parts.hour}:${parts.minute}`;
    case 'LTS':
      return `${parts.hour}:${parts.minute}:${parts.second}`;
    case 'YYYY':
      return parts.year;
    case 'MMMM':
      return new Intl.DateTimeFormat(locale, { timeZone, month: 'long' }).format(date);
    case 'MMMM YYYY':
      return new Intl.DateTimeFormat(locale, { timeZone, month: 'long', year: 'numeric' }).format(date);
    case 'ddd':
      return new Intl.DateTimeFormat(locale, { timeZone, weekday: 'short' }).format(date);
    case 'dddd':
      return new Intl.DateTimeFormat(locale, { timeZone, weekday: 'long' }).format(date);
    case 'LL':
      return new Intl.DateTimeFormat(locale, { timeZone, dateStyle: 'long' }).format(date);
    case 'LLL':
      return new Intl.DateTimeFormat(locale, {
        timeZone,
        dateStyle: 'long',
        timeStyle: 'short',
      }).format(date);
    case 'MMMM Do YYYY':
      return formatOrdinalLongDate(date, timeZone, locale, parts);
    case 'L':
    default:
      return new Intl.DateTimeFormat(locale, {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
  }
}

function numericParts(date: Date, timeZone: string): DateParts {
  const parts = new Intl.DateTimeFormat('en-GB-u-nu-latn', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const byType = new Map(parts.map(part => [part.type, part.value]));

  return {
    year: byType.get('year') ?? '',
    month: byType.get('month') ?? '',
    day: byType.get('day') ?? '',
    hour: byType.get('hour') ?? '',
    minute: byType.get('minute') ?? '',
    second: byType.get('second') ?? '',
  };
}

function formatOrdinalLongDate(
  date: Date,
  timeZone: string,
  locale: string,
  parts: DateParts,
): string {
  if (!locale.toLowerCase().startsWith('en')) {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }

  const day = Number(parts.day);
  const mod100 = day % 100;
  const suffix = mod100 >= 11 && mod100 <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ?? 'th';
  const month = new Intl.DateTimeFormat(locale, { timeZone, month: 'long' }).format(date);
  return `${month} ${day}${suffix} ${parts.year}`;
}
